import { X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "react-query";
import CustomInput from "../../shared/CustomInput";
import { getLoanProducts } from "../../sdk/products/products";
import CustomArea from "../../shared/CustomArea";

const LoanAmount = ({
  closeLoanAmount,
  handleCollectLoanTypeInformation,
  loanDetails,
  setLoanDetails,
}) => {
  const [formErrors, setFormErrors] = useState({});

  const { data: loanProducts } = useQuery({
    queryKey: ["loan-products"],
    queryFn: async () => {
      const response = await getLoanProducts();
      return response.data;
    },
    initialData: [],
  });

  const validateForm = () => {
    const errors = {};

    if (!loanDetails?.loanProduct) {
      errors.loanProduct = "Loan type is required";
    }

    if (!loanDetails?.loanAmount) {
      errors.loanAmount = "Loan amount is required";
    } else if (isNaN(loanDetails.loanAmount)) {
      errors.loanAmount = "Loan amount must be a number";
    } else if (selectedProduct) {
      const amount = Number(loanDetails.loanAmount);
      if (amount < selectedProduct.minLoanAmount) {
        errors.loanAmount = `Loan amount cannot be less than ${selectedProduct.minLoanAmount}`;
      } else if (amount > selectedProduct.maxLoanAmount) {
        errors.loanAmount = `Loan amount cannot be more than ${selectedProduct.maxLoanAmount}`;
      }
    }

    if (!loanDetails?.loanDuration) {
      errors.loanDuration = "Loan duration is required";
    } else if (isNaN(loanDetails.loanDuration)) {
      errors.loanDuration = "Loan duration must be a number";
    } else if (selectedProduct) {
      const duration = Number(loanDetails.loanDuration);
      if (duration < selectedProduct.minLoanPeriod) {
        errors.loanDuration = `Loan duration cannot be less than ${selectedProduct.minLoanPeriod} months`;
      } else if (duration > selectedProduct.maxLoanPeriod) {
        errors.loanDuration = `Loan duration cannot be more than ${selectedProduct.maxLoanPeriod} months`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const selectedProduct = loanProducts.find(
    (p) => p.id === loanDetails?.loanProduct,
  );

  const handleSubmitLoanDetails = () => {
    if (validateForm()) {
      handleCollectLoanTypeInformation();
    }
  };

  return (
    <div
      onClick={(e) => {
        closeLoanAmount();
      }}
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[800px] h-[500px] rounded-[10px] p-[15px] relative bg-white"
      >
        <div
          onClick={closeLoanAmount}
          className="absolute top-[0px] cursor-pointer h-[40px] w-[40px] bg-[#D63B3B] rounded-tr-[10px] rounded-bl-[20px] right-[0px] flex items-center justify-center"
        >
          <X color="#ffffff" />
        </div>
        <div className="absolute bottom-[20px] right-[20px]">
          <button
            onClick={handleSubmitLoanDetails}
            className="h-[35px] bg-[#000000] rounded-[30px] px-[15px] text-[15px] font-bold text-white"
          >
            Next
          </button>
        </div>
        <div>
          <p className="text-[#000000] text-[20px] font-semibold">
            Create A New Loan Application
          </p>
          <p className="text-[#5C5C5C] font-normal text-[14px]">
            Select the loan type the borrower is interested in and loan amount
          </p>
          <div className="flex items-start gap-[20px] my-[20px]">
            <div className="flex w-[47%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="loan-type"
              >
                Loan Type
              </label>
              <select
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9] bg-white"
                name="loanType"
                value={loanDetails?.loanProduct}
                onChange={(e) =>
                  setLoanDetails({
                    ...loanDetails,
                    loanProduct: e.target.value,
                  })
                }
              >
                <option value="">Select a product</option>
                {loanProducts?.map((option) => (
                  <option key={option?.id} value={option?.id}>
                    {option?.name}
                  </option>
                ))}
              </select>
              {formErrors.loanProduct && (
                <span className="text-red-500 text-[12px] mt-1">
                  {formErrors.loanProduct}
                </span>
              )}
            </div>
            <div className="flex w-[47%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="loan-type"
              >
                Loan Amount
              </label>
              <CustomInput
                placeholder="Enter loan amount"
                type="text"
                value={loanDetails?.loanAmount}
                onChange={(e) =>
                  setLoanDetails({ ...loanDetails, loanAmount: e.target.value })
                }
              />
              {selectedProduct && (
                <span className="text-[#5C5C5C] text-[12px] mt-1">
                  Minimum: {selectedProduct.minLoanAmount} | Maximum:{" "}
                  {selectedProduct.maxLoanAmount}
                </span>
              )}
              {formErrors.loanAmount && (
                <span className="text-red-500 text-[12px] mt-1">
                  {formErrors.loanAmount}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-[20px] my-[20px]">
            <div className="flex w-[47%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="loan-duration"
              >
                Loan Duration(months)
              </label>
              <CustomInput
                placeholder="Enter loan duration(month)"
                type="text"
                value={loanDetails?.loanDuration}
                onChange={(e) =>
                  setLoanDetails({
                    ...loanDetails,
                    loanDuration: e.target.value,
                  })
                }
              />
              {selectedProduct && (
                <span className="text-[#5C5C5C] text-[12px] mt-1">
                  Minimum: {selectedProduct.minLoanPeriod} months | Maximum:{" "}
                  {selectedProduct.maxLoanPeriod} months
                </span>
              )}
              {formErrors.loanDuration && (
                <span className="text-red-500 text-[12px] mt-1">
                  {formErrors.loanDuration}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-[20px] my-[20px]">
            <div className="flex w-[47%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="loan-duration"
              >
                Loan Purpose
              </label>
              <CustomArea
                placeholder="What is the purpose of the loan?"
                type="text"
                value={loanDetails?.purpose}
                onChange={(e) =>
                  setLoanDetails({
                    ...loanDetails,
                    purpose: e.target.value,
                  })
                }
              />
              {formErrors.purpose && (
                <span className="text-red-500 text-[12px] mt-1">
                  {formErrors.purpose}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanAmount;
